import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Popover,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Switch,
  InputAdornment,
  Divider,
  FormControlLabel,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQCompany,
  useTSQPeopleList,
  useTSMCreatePerson,
  useTSMUpdatePerson,
  useTSMCreateCompany,
  useTSMUpdateCompany,
} from "../../hooks/useTSEntities";
import {
  useTSQAddressList,
  useTSMUpdateAddress,
  useTSMCreateAddress,
} from "../../hooks/useTSAddresses";
import { ShippingMethods } from "../../types/common/termTypes";
import {
  PAYMENT_TERMS,
  PaymentTermType,
  CUST_SHIP_BILLING_TYPES,
  CustomerShipmentBillingType,
  AddressType,
} from "../../types/common/termTypes";
import { SalesOrderFulfillmentStatusType } from "../../types/common/statusTypes";
import {
  VendorCreate,
  VendorUpdate,
  CustomerCreate,
  CustomerUpdate,
  CUSTOMER_TYPES,
  CompanyType,
  CustomerFull,
  VendorFull,
  CustomerType,
  CompanyFullGroup,
} from "../../types/companyTypes";
import { ContactCreate } from "../../types/personTypes";
import {
  AddressCreate,
  AddressFull,
  AddressUpdate,
  PaginatedAddressResponse,
} from "../../types/addressTypes";
import { WorldLocation } from "../../types/common/commonTypes";
import {
  TypedSelectOption,
  CountryOption,
  StateOption,
  CityOption,
  EmployeeOption,
  YupTypedSelectOption,
} from "../../types/common/selectOptions";
import { useError } from "../../contexts/ErrorContextManager";
import stringUtils from "../../utils/stringUtils";
import { useWorldLocationData } from "../../contexts/WorldLocationContextProvider";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import {
  NumericDisplayField,
  NumericEntryField,
} from "../../components/common/CustomFields";
import { useTSQPurchOrdersByVendor } from "../../hooks/useTSPurchaseOrder";
import { useTSQSalesOrdersByCustomer } from "../../hooks/useTSSalesOrder";
import statusUtils from "../../utils/statusUtils";
import { PurchaseOrderReceiptStatusType } from "../../types/purchOrderTypes";

type ShippingProviderOption = TypedSelectOption<"shipping_provider">;

const shippingProviderOptions: ShippingProviderOption[] = [
  { type: "shipping_provider", label: "FedEx", value: { id: "FEDEX" } },
  { type: "shipping_provider", label: "UPS", value: { id: "UPS" } },
  { type: "shipping_provider", label: "Any", value: { id: "ANY" } },
];

type ShipmentMethodOption = TypedSelectOption<
  "shipment_method",
  { provider: string }
>;

const [fedexOpts, upsOpts] = (() => {
  let fedexOpts: ShipmentMethodOption[] = ShippingMethods.fedex.methods.map(
    (m) => ({
      label: m,
      type: "shipment_method",
      value: {
        provider: "FedEx",
        id: m.toUpperCase(),
      },
    }),
  );
  let upsOpts: ShipmentMethodOption[] = ShippingMethods.ups.methods.map(
    (m) => ({
      label: m,
      type: "shipment_method",
      value: {
        provider: "UPS",
        id: m.toUpperCase(),
      },
    }),
  );
  return [fedexOpts, upsOpts];
})();

type PaymentTermOption = TypedSelectOption<"payment_term", {}, PaymentTermType>;

const paymentTermOptions: PaymentTermOption[] = (() => {
  return PAYMENT_TERMS.filter((a) => true)
    .sort((a, b) => a.localeCompare(b))
    .map((val) => ({
      label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
      type: "payment_term",
      value: { id: val },
    }));
})();

type CustomerTypeOption = TypedSelectOption<"customer_type", {}, CustomerType>;

const customerTypeOptions: CustomerTypeOption[] = (() => {
  return CUSTOMER_TYPES.filter((a) => true)
    .sort((a, b) => a.localeCompare(b))
    .map((val) => ({
      label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
      type: "customer_type",
      value: { id: val },
    }));
})();

type CustomerShipmentBillingOption = TypedSelectOption<
  "shipment_billing",
  {},
  CustomerShipmentBillingType
>;

const customerShipmentBillingOptions: CustomerShipmentBillingOption[] = (() => {
  return CUST_SHIP_BILLING_TYPES.filter((a) => true)
    .sort((a, b) => a.localeCompare(b))
    .map((val) => ({
      label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
      type: "shipment_billing",
      value: { id: val },
    }));
})();

type AddressTypeOption = TypedSelectOption<"address_type", {}, AddressType>;

const addressTypeOptions: AddressTypeOption[] = [
  { type: "address_type", label: "Billing", value: { id: "BILLING" } },
  { type: "address_type", label: "Shipping", value: { id: "SHIPPING" } },
  { type: "address_type", label: "Primary", value: { id: "PRIMARY" } },
  { type: "address_type", label: "Other", value: { id: "OTHER" } },
];

type ContactTypeOption = TypedSelectOption<"contact_type">;

const contactTypeOptions: ContactTypeOption[] = [
  { type: "contact_type", label: "Billing", value: { id: "BILLING" } },
  { type: "contact_type", label: "Shipping", value: { id: "SHIPPING" } },
  { type: "contact_type", label: "Primary", value: { id: "PRIMARY" } },
  { type: "contact_type", label: "Other", value: { id: "OTHER" } },
];

type AEmployeeOption = EmployeeOption<{}>;

type ACountryOption = CountryOption<{ code: string }>;
type AStateOption = StateOption<{ code: string; country_id: number }>;
type ACityOption = CityOption<{ country_id: number; state_id: number }>;

interface AddrData {
  addr_id: string | null;
  name: string;
  addressee: string | null;
  attn: string | null;
  line_1: string;
  line_2: string | null;
  line_3: string | null;
  city: ACityOption | null;
  state: AStateOption | null;
  country: ACountryOption | null;
  zip: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  is_preferred: boolean;
  address_type: AddressTypeOption;
  is_delete: boolean;
  is_new: boolean;
  is_active: boolean;
}

const defaultAddrData: AddrData = {
  addr_id: null,
  name: "",
  addressee: null,
  attn: null,
  line_1: "",
  line_2: null,
  line_3: null,
  city: null,
  state: null,
  country: null,
  zip: "",
  phone: null,
  email: null,
  notes: null,
  is_preferred: false,
  address_type: addressTypeOptions[2],
  is_delete: false,
  is_new: true,
  is_active: true,
};

interface AddrDataErrors {
  name: string | null;
  addressee: string | null;
  attn: string | null;
  line_1: string | null;
  line_2: string | null;
  line_3: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  is_preferred: string | null;
  address_type: string | null;
}

const defaultAddrDataErrors: AddrDataErrors = {
  name: null,
  addressee: null,
  attn: null,
  line_1: null,
  line_2: null,
  line_3: null,
  city: null,
  state: null,
  country: null,
  zip: null,
  phone: null,
  email: null,
  notes: null,
  is_preferred: null,
  address_type: null,
};

interface ContactData {
  id: string | null;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  job_title: string | null;
  contact_type: ContactTypeOption | null;
  notes: string | null;
  is_delete: boolean;
  is_new: boolean;
}

interface CompanyFormData {
  // Customer & Vendor
  name: string;
  code: string;
  legal_name: string;
  default_payment_terms: PaymentTermOption | null;
  credit_limit: number;
  preferred_shipping_provider: ShippingProviderOption | null;
  preferred_shipping_methods: {
    fedex: ShipmentMethodOption | null;
    ups: ShipmentMethodOption | null;
  };
  centese_contact: AEmployeeOption | null;
  box_folder_id: string | null;
  notes: string | null;

  addresses: AddrData[];

  contacts: ContactData[];

  // Customer Only
  customer_type: CustomerTypeOption | null;
  shipping_accounts: { fedex: string | null; ups: string | null };
  shipment_billing_preference: CustomerShipmentBillingOption | null;
}

const formDefaults: CompanyFormData = {
  name: "",
  code: "",
  legal_name: "",
  default_payment_terms: null,
  preferred_shipping_provider: null,
  preferred_shipping_methods: { fedex: null, ups: null },
  centese_contact: null,
  box_folder_id: null,
  notes: null,
  addresses: [],
  contacts: [],
  customer_type: null,
  credit_limit: 0,
  shipping_accounts: { fedex: null, ups: null },
  shipment_billing_preference: null,
};

// Validation schema
const companySchema = yup.object({
  name: yup
    .string()
    .required("Name is Required")
    .test({
      name: "check-legal-chars-name",
      test: (value, context) => {
        const matches = value.match(/(^[.]{1,})|([\\])|([/])/g);
        if (matches !== null) {
          let pStart = false;
          let fSlash = false;
          let bSlash = false;
          for (let m of matches) {
            if (m.includes(".")) {
              pStart = true;
              continue;
            }
            if (m === "/") {
              fSlash = true;
              continue;
            }
            if (m === "\\") {
              bSlash = true;
            }
          }
          return context.createError({
            path: "name",
            message: `Name contains illegal characters:${pStart ? " Cannot start with '.'" : ""}${fSlash ? " Cannot contain '/'" : ""}${bSlash ? " Cannot contain '\\'" : ""}`,
          });
        }
        return true;
      },
    }),
  code: yup.string().optional().nullable().defined(),
  legal_name: yup
    .string()
    .required("Legal Name required")
    .test({
      name: "check-legal-chars-legalname",
      test: (value, context) => {
        const matches = value.match(/(^[.]{1,})|([\\])|([/])/g);
        if (matches !== null) {
          let pStart = false;
          let fSlash = false;
          let bSlash = false;
          for (let m of matches) {
            if (m.includes(".")) {
              pStart = true;
              continue;
            }
            if (m === "/") {
              fSlash = true;
              continue;
            }
            if (m === "\\") {
              bSlash = true;
            }
          }
          return context.createError({
            path: "legal_name",
            message: `Legal Name contains illegal characters:${pStart ? " Cannot start with '.'" : ""}${fSlash ? " Cannot contain '/'" : ""}${bSlash ? " Cannot contain '\\'" : ""}`,
          });
        }
        return true;
      },
    }),
  default_payment_terms: YupTypedSelectOption.required(
    "Default Payment Terms required.",
  ),
  preferred_shipping_provider: YupTypedSelectOption.nullable().test({
    name: "preferred-shipping-provider",
    test: (value, context) => {
      const compType: string = context.options.context?.compType || "";
      if (compType === "") {
        return context.createError({
          path: "company_type",
          message:
            "Unable to determine company type, refresh the page and try again or notify IT.",
        });
      }
      if (compType === "customer") {
        if (!value) {
          return context.createError({
            path: "preferred_shipping_provider",
            message: "Preferred Shipping Provider is Required for Customers",
          });
        }
      }
      return true;
    },
  }),
  preferred_shipping_methods: yup
    .object({
      fedex: YupTypedSelectOption.nullable().defined(),
      ups: YupTypedSelectOption.nullable().defined(),
    })
    .test({
      name: "preferred-shipping-methods",
      test: (value, context) => {
        const compType: string = context.options.context?.compType || "";
        if (compType === "") {
          return context.createError({
            path: "company_type",
            message:
              "Unable to determine company type, refresh the page and try again or notify IT.",
          });
        }
        if (compType === "customer") {
          const provider: ShippingProviderOption | null =
            context.parent.preferred_shipping_provider || null;
          if (!provider) {
            return context.createError({
              path: "preferred_shipping_provider",
              message: "Preferred Shipping Provider is Required for Customers",
            });
          }
          if ((provider.label as string).toLowerCase() === "fedex") {
            if (!value.fedex) {
              return context.createError({
                message:
                  "Must Select shipping method for Selected Preferred Shipping Provider",
                path: "preferred_shipping_methods.fedex",
              });
            }
          } else if ((provider.label as string).toLowerCase() === "ups") {
            if (!value.ups) {
              return context.createError({
                message:
                  "Must Select shipping method for Selected Preferred Shipping Provider",
                path: "preferred_shipping_methods.ups",
              });
            }
          }
        }
        return true;
      },
    }),
  centese_contact: YupTypedSelectOption.nullable().test({
    name: "sales-rep",
    test: (value, context) => {
      const compType: string = context.options.context?.compType || "";
      if (compType === "") {
        return context.createError({
          path: "company_type",
          message:
            "Unable to determine company type, refresh the page and try again or notify IT.",
        });
      }
      if (compType === "customer") {
        if (!value) {
          return context.createError({
            path: "centese_contact",
            message: "Sales Rep is Required for Customers",
          });
        }
      }
      return true;
    },
  }),
  box_folder_id: yup.string().nullable().defined(),
  notes: yup.string().nullable().defined(),

  addresses: yup
    .array()
    .of(
      yup.object({
        addr_id: yup.string().nullable().defined(),
        name: yup.string().required("An Address Name is Required"),
        addressee: yup.string().nullable().defined(),
        attn: yup.string().nullable().defined(),
        line_1: yup.string().required("Address Line 1 is Required"),
        line_2: yup.string().nullable().defined(),
        line_3: yup.string().nullable().defined(),
        city: YupTypedSelectOption.required("City is Required"),
        state: YupTypedSelectOption.required("State is Required"),
        country: YupTypedSelectOption.required("Country is Required"),
        zip: yup.string().required("Zip is Required"),
        phone: yup.string().nullable().defined(),
        email: yup.string().nullable().defined(),
        notes: yup.string().nullable().defined(),
        is_preferred: yup.boolean().required(),
        address_type: YupTypedSelectOption.required("Address Type is Required"),
        is_delete: yup.boolean().required(),
        is_new: yup.boolean().required(),
      }),
    )
    .min(1, "At least one address is required"),

  contacts: yup.array().of(
    yup.object({
      id: yup.string().required(),
      first_name: yup.string().required(),
      last_name: yup.string().required(),
      title: yup.string().nullable().defined(),
      email: yup.string().nullable().defined(),
      phone: yup.string().nullable().defined(),
      job_title: yup.string().nullable().defined(),
      contact_type: yup.object({
        type: yup
          .string()
          .required()
          .test((value) => value === "contact_type"),
        label: yup.string().required(),
        value: yup.object({ id: yup.string().required() }).required(),
      }),
      notes: yup.string().nullable().defined(),
      is_delete: yup.boolean().required(),
      is_new: yup.boolean().required(),
    }),
  ),
  credit_limit: yup.number().required("Credit Limit is Required"),

  // Customer Only
  customer_type: YupTypedSelectOption.nullable()
    .defined()
    .test({
      name: "customer-type",
      test: (value, context) => {
        const compType: string = context.options.context?.compType || "";
        if (compType === "") {
          return context.createError({
            path: "company_type",
            message:
              "Unable to determine company type, refresh the page and try again or notify IT.",
          });
        }
        if (compType === "customer") {
          if (!value) {
            return context.createError({
              path: "customer_type",
              message: "Customer Type is Required",
            });
          }
        }
        return true;
      },
    }),
  shipping_accounts: yup
    .object({
      fedex: yup.string().nullable().defined(),
      ups: yup.string().nullable().defined(),
    })
    .test({
      name: "check-pref-carrier-shipping-account",
      test: function (value, context) {
        const compType: string = context.options.context?.compType || "";
        if (compType === "") {
          return context.createError({
            path: "company_type",
            message:
              "Unable to determine company type, refresh the page and try again or notify IT.",
          });
        }
        if (compType === "customer") {
          const billOpt = context.parent.shipment_billing_preference;
          if (!billOpt) {
            return context.createError({
              message: "Must select Shipment Billing Option",
              path: "shipment_billing_preference",
            });
          }
          if (billOpt.label !== "Customer") {
            return true;
          }
          const preferred_shipping_provider =
            context.parent.preferred_shipping_provider;
          if (
            (preferred_shipping_provider?.label as string).toLowerCase() ===
            "fedex"
          ) {
            if (!value.fedex) {
              return context.createError({
                message:
                  "Must provide shipping account for Preferred Shipping Provider when shipment billing preference is set to 'Customer'",
                path: "shipping_accounts.fedex",
              });
            }
          } else if (
            (preferred_shipping_provider?.label as string).toLowerCase() ===
            "ups"
          ) {
            if (!value.ups) {
              return context.createError({
                message:
                  "Must provide shipping account for Preferred Shipping Provider when shipment billing preference is set to 'Customer'",
                path: "shipping_accounts.ups",
              });
            }
          } else {
            if (!value.ups && !value.fedex) {
              return context.createError({
                message:
                  "Must provide at least one shipping account when shipment billing preference is set to 'Customer'",
                path: "shipping_accounts",
              });
            }
          }
        }
        return true;
      },
    }),
  shipment_billing_preference: YupTypedSelectOption.nullable()
    .defined()
    .test({
      name: "shipment-billing",
      test: (value, context) => {
        const compType: string = context.options.context?.compType || "";
        if (compType === "") {
          return context.createError({
            path: "company_type",
            message:
              "Unable to determine company type, refresh the page and try again or notify IT.",
          });
        }
        if (compType === "customer") {
          if (!value) {
            return context.createError({
              path: "shipment_billing_preference",
              message: "Shipment Billing Preference is Required",
            });
          }
        }
        return true;
      },
    }),
});

const AddressDialog: React.FC<{
  open: boolean;
  prevAddrData: AddrData | null;
  onCancel: () => void;
  onSubmit: (data: AddrData) => void;
  countryOpts: ACountryOption[];
  stateOpts: AStateOption[];
  cityOpts: ACityOption[];
}> = ({
  open,
  prevAddrData,
  onCancel,
  onSubmit,
  countryOpts,
  stateOpts,
  cityOpts,
}) => {
  const [thisAddrData, setThisAddrData] = useState<AddrData>(defaultAddrData);
  const [dialogSubmitting, setDialogSubmitting] = useState<boolean>(false);
  const [thisAddrErrors, setThisAddrErrors] = useState<AddrDataErrors>(
    defaultAddrDataErrors,
  );

  const filteredStateOpts = useMemo(() => {
    if (thisAddrData.country) {
      return stateOpts.filter(
        (s) => s.value.country_id === thisAddrData.country?.value.id,
      );
    } else {
      return [] as AStateOption[];
    }
  }, [thisAddrData.country, stateOpts]);

  const filteredCityOpts = useMemo(() => {
    if (thisAddrData.country && thisAddrData.state) {
      return cityOpts.filter(
        (c) =>
          c.value.country_id === thisAddrData.country?.value.id &&
          c.value.state_id === thisAddrData.state?.value.id,
      );
    } else {
      return [] as ACityOption[];
    }
  }, [thisAddrData.country, thisAddrData.state, cityOpts]);

  useEffect(() => {
    if (open) {
      if (prevAddrData) {
        setThisAddrData(prevAddrData);
      } else {
        setThisAddrData({
          ...defaultAddrData,
          country:
            countryOpts.find((o) => o.value.code === "US") ||
            defaultAddrData.country,
        });
      }
      setThisAddrErrors(defaultAddrDataErrors);
    }
  }, [open, prevAddrData, countryOpts]);

  const handleDialogCancel = () => {
    onCancel();
  };

  const validateAddrData = () => {
    setDialogSubmitting(true);
    // Fresh copy: writing into defaultAddrDataErrors itself left stale errors
    // on every address dialog opened afterwards.
    const theseErrs = { ...defaultAddrDataErrors };
    if (!thisAddrData.line_1) {
      theseErrs.line_1 = "Addresses require a line 1";
    }
    if (!thisAddrData.country) {
      theseErrs.country = "Addresses require a Country";
    }
    if (!thisAddrData.state) {
      theseErrs.state = "Addresses require a State";
    }
    if (!thisAddrData.city) {
      theseErrs.city = "Addresses require a City";
    }
    if (!thisAddrData.zip) {
      theseErrs.zip = "Addresses require a Zip/Postal Code";
    }
    if (!thisAddrData.address_type) {
      theseErrs.address_type = "Addresses require a Type";
    }
    setThisAddrErrors(theseErrs);
    if (Object.values(theseErrs).some((v) => !!v)) {
      // Re-enable Save/Cancel so the missing field can be filled in. Returning
      // with the buttons still disabled trapped the user in the dialog.
      setDialogSubmitting(false);
      return;
    }
    handleDialogSubmit();
  };

  const handleDialogSubmit = () => {
    console.log("Submitting Address Data", thisAddrData);
    onSubmit(thisAddrData);
    setDialogSubmitting(false);
  };

  const handleCountryChange = (country: ACountryOption | null) => {
    if (country) {
      setThisAddrData((prev) => ({
        ...prev,
        country: country,
        state: null,
        city: null,
      }));
    } else {
      setThisAddrData((prev) => ({
        ...prev,
        country: null,
        state: null,
        city: null,
      }));
    }
  };

  const handleStateChange = (state: AStateOption | null) => {
    if (state) {
      setThisAddrData((prev) => ({
        ...prev,
        state: state,
        city: null,
      }));
    } else {
      setThisAddrData((prev) => ({
        ...prev,
        state: null,
        city: null,
      }));
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        {thisAddrData.is_new ? "Add Address" : "Edit Address"}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" gap={1} p={3}>
          {thisAddrData.is_delete && (
            <Alert variant="filled" severity="warning">
              This Address is Marked for Deletion
            </Alert>
          )}
          <TextField
            value={thisAddrData.name}
            size="small"
            onChange={(e) => {
              setThisAddrData((prev) => ({
                ...prev,
                name: e.target.value,
              }));
            }}
            label="Address Name"
            error={!!thisAddrErrors.name}
            helperText={
              thisAddrErrors.name
                ? thisAddrErrors.name
                : "This is a label for the Address, IT IS NOT INCLUDED IN THE ADDRESS IT IS JUST A WAY TO IDENTIFY IT"
            }
          />
          <Box display={"flex"} gap={2} width="100%">
            <Stack direction="column" gap={1} width="50%">
              <TextField
                fullWidth
                size="small"
                value={thisAddrData.addressee}
                onChange={(e) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    addressee: e.target.value,
                  }));
                }}
                label="Addressee"
                error={!!thisAddrErrors.addressee}
                helperText={
                  thisAddrErrors.addressee
                    ? thisAddrErrors.addressee
                    : "Also called 'Company Name'."
                }
              />
              <TextField
                fullWidth
                size="small"
                value={thisAddrData.line_1}
                onChange={(e) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    line_1: e.target.value,
                  }));
                }}
                label="Line 1"
                error={!!thisAddrErrors.line_1}
                helperText={thisAddrErrors.line_1}
              />
              <TextField
                fullWidth
                size="small"
                value={thisAddrData.line_2}
                onChange={(e) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    line_2: e.target.value,
                  }));
                }}
                label="Line 2"
                error={!!thisAddrErrors.line_2}
                helperText={thisAddrErrors.line_2}
              />
              <TextField
                fullWidth
                size="small"
                value={thisAddrData.line_3}
                onChange={(e) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    line_3: e.target.value,
                  }));
                }}
                label="Line 3"
                error={!!thisAddrErrors.line_3}
                helperText={thisAddrErrors.line_3}
              />
              <TextField
                fullWidth
                size="small"
                value={thisAddrData.phone}
                onChange={(e) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }));
                }}
                label="Phone"
                error={!!thisAddrErrors.phone}
                helperText={thisAddrErrors.phone}
              />
              <FormControlLabel
                control={
                  <Switch
                    value={thisAddrData.is_preferred}
                    size="small"
                    onChange={(e, c) => {
                      setThisAddrData((prev) => ({
                        ...prev,
                        is_preferred: c,
                      }));
                    }}
                  />
                }
                label="Preferred Address?"
              />
            </Stack>
            <Stack direction="column" gap={1} width="50%">
              <TextField
                fullWidth
                size="small"
                value={thisAddrData.attn}
                onChange={(e) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    attn: e.target.value,
                  }));
                }}
                label="Attention"
                error={!!thisAddrErrors.attn}
                helperText={thisAddrErrors.attn}
              />
              <Autocomplete
                fullWidth
                value={thisAddrData.country}
                options={countryOpts}
                onChange={(e, v) => {
                  handleCountryChange(v);
                }}
                getOptionLabel={(opt) => opt?.label || ""}
                isOptionEqualToValue={(opt, val) =>
                  opt.value.id === val.value.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    error={!!thisAddrErrors.country}
                    helperText={thisAddrErrors.country}
                    size="small"
                  />
                )}
              />
              <Autocomplete
                fullWidth
                value={thisAddrData.state}
                options={filteredStateOpts}
                onChange={(e, v) => {
                  handleStateChange(v);
                }}
                getOptionLabel={(opt) => opt?.label || ""}
                isOptionEqualToValue={(opt, val) =>
                  opt.value.id === val.value.id
                }
                disabled={!thisAddrData.country}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    error={!!thisAddrErrors.state}
                    helperText={thisAddrErrors.state}
                    size="small"
                  />
                )}
              />
              <Autocomplete
                fullWidth
                value={thisAddrData.city}
                options={filteredCityOpts}
                onChange={(e, v) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    city: v,
                  }));
                }}
                getOptionLabel={(opt) => opt?.label || ""}
                isOptionEqualToValue={(opt, val) =>
                  opt.value.id === val.value.id
                }
                disabled={!thisAddrData.state}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    error={!!thisAddrErrors.city}
                    helperText={
                      thisAddrErrors.city
                        ? thisAddrErrors.city
                        : !thisAddrData.state
                          ? "Select State to select a City"
                          : undefined
                    }
                    size="small"
                  />
                )}
              />
              <TextField
                fullWidth
                value={thisAddrData.zip}
                onChange={(e) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    zip: e.target.value,
                  }));
                }}
                label="Zip/Postal Code"
                error={!!thisAddrErrors.zip}
                helperText={thisAddrErrors.zip}
                size="small"
              />
              <TextField
                fullWidth
                value={thisAddrData.email}
                onChange={(e) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }));
                }}
                label="Email"
                error={!!thisAddrErrors.email}
                helperText={thisAddrErrors.email}
                size="small"
              />
              <Autocomplete
                fullWidth
                value={thisAddrData.address_type}
                options={addressTypeOptions}
                onChange={(e, v) => {
                  setThisAddrData((prev) => ({
                    ...prev,
                    address_type: v ? v : addressTypeOptions[2],
                  }));
                }}
                getOptionLabel={(opt) => opt?.label || ""}
                isOptionEqualToValue={(opt, val) =>
                  opt.value.id === val.value.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Address Type"
                    error={!!thisAddrErrors.address_type}
                    helperText={thisAddrErrors.address_type}
                    size="small"
                  />
                )}
              />
            </Stack>
          </Box>
          <TextField
            value={thisAddrData.notes}
            onChange={(e) => {
              setThisAddrData((prev) => ({
                ...prev,
                notes: e.target.value,
              }));
            }}
            label="Notes"
            error={!!thisAddrErrors.notes}
            helperText={thisAddrErrors.notes}
            size="small"
            multiline
            maxRows={4}
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogCancel} disabled={dialogSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={validateAddrData}
          variant="contained"
          disabled={dialogSubmitting}
        >
          {thisAddrData.is_new ? "Add Address" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ContactDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (data: ContactData) => void;
  initialData: ContactData | null;
}> = ({ open, onClose, onSave, initialData }) => {
  const isEditMode = initialData !== null;
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<ContactData>({
    defaultValues: initialData || {
      id: null,
      first_name: null,
      last_name: null,
      title: null,
      email: null,
      phone: null,
      job_title: null,
      contact_type: null,
      notes: null,
      is_delete: false,
      is_new: true,
    },
  });
  useEffect(() => {
    if (initialData) {
      reset(initialData);
    } else {
      reset({
        id: null,
        first_name: null,
        last_name: null,
        title: null,
        email: null,
        phone: null,
        job_title: null,
        contact_type: null,
        notes: null,
        is_delete: false,
      });
    }
  }, [initialData, reset]);

  const handleSave = (data: ContactData) => {
    onSave(data);
    onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? "Edit Contact" : "Add Contact"}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ mt: 2 }}
          onSubmit={handleSubmit(handleSave, console.error)}
        >
          <Stack spacing={2}>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: "First Name required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  error={!!errors.first_name}
                  helperText={
                    errors.first_name ? errors.first_name.message : ""
                  }
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{ required: "Last Name required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  error={!!errors.last_name}
                  helperText={errors.last_name ? errors.last_name.message : ""}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title ? errors.title.message : ""}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email ? errors.email.message : ""}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone ? errors.phone.message : ""}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="job_title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Job Title"
                  fullWidth
                  error={!!errors.job_title}
                  helperText={errors.job_title ? errors.job_title.message : ""}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="contact_type"
              control={control}
              rules={{ required: "Contact Type required" }}
              render={({ field: { value, onChange, ...restField } }) => (
                <Autocomplete
                  {...restField}
                  value={value}
                  onChange={(_, data) => onChange(data)}
                  options={contactTypeOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value.id === value.value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contact Type"
                      error={!!errors.contact_type}
                      helperText={
                        errors.contact_type ? errors.contact_type.message : ""
                      }
                      disabled={isSubmitting}
                      fullWidth
                    />
                  )}
                />
              )}
            />
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.notes}
                  helperText={errors.notes ? errors.notes.message : ""}
                  disabled={isSubmitting}
                />
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(handleSave)}
          variant="contained"
          disabled={isSubmitting}
        >
          {isEditMode ? "Save Changes" : "Add Contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OrderFilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
  mode: string;
}> = ({ finalSet, mode }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search Orders"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder={`Search Orders by ${
        mode === "customer" ? "SO" : "PO"
      } Number`}
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PageCompanyFormV2: React.FC = () => {
  const navigate = useNavigate();

  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Mode detection from URL query parameters
  // const searchParams = new URLSearchParams(location.search);
  const recId = searchParams.get("id");
  const formMode = searchParams.get("mode");
  if (!formMode) {
    throw new Error("Unable to determine form mode");
  }

  const refFormMode = useRef<string>(formMode);

  const companyType = searchParams.get("type");
  if (!companyType) {
    throw new Error("Unable to determine company type, cannot open form.");
  }
  const companyTypePretty = stringUtils.capFirstLetter(companyType);
  const refCompanyType = useRef(companyType);
  const refRenderCount = useRef(0);

  const refOrigData = useRef<CompanyFullGroup | null>(null);
  const refOrigAddrs = useRef<AddressFull[]>([]);

  useEffect(() => {
    console.log(`Render # ${++refRenderCount.current}`);
    refFormMode.current = formMode;
    refCompanyType.current = companyType;
  });

  // Form State
  const [errorText, setErrorText] = useState<string | null>(null);

  const [overrideCodeGen, setOverrideCodeGen] = useState<boolean>(false);
  const [overrideLegalName, setOverrideLegalName] = useState<boolean>(false);

  let onLegalNameChange: any = null;

  const [orderTableSort, setOrderTableSort] = useState<string>("order_num");
  const [orderTableSortDir, setOrderTableSortDir] = useState<"asc" | "desc">(
    "desc",
  );
  const [orderTablePagination, setOrderTablePagination] = useState<{
    page: number;
    size: number;
  }>({
    page: 0,
    size: 25,
  });

  const [curTab, setCurTab] = useState<number>(0);

  const createCompanyMutation = useTSMCreateCompany();
  const updateCompanyMutation = useTSMUpdateCompany();

  const {
    data: empsResponse,
    isLoading: empsLoading,
    isFetching: empsFetching,
    error: empsError,
  } = useTSQPeopleList(
    {
      filters: [[{ field: "sales_rep", operator: "==", value: true }]],
    },
    "employee",
    true,
  );
  const empOpts: AEmployeeOption[] = useMemo(() => {
    if (empsResponse?.items) {
      return empsResponse.items.map((e) => ({
        label: `${e.first_name} ${e.last_name}`,
        type: "employee",
        value: {
          id: e.id,
        },
      }));
    } else {
      return [];
    }
  }, [empsResponse]);

  const {
    data: thisCompanyData,
    isLoading: thisCompanyLoading,
    isFetching: thisCompanyFetching,
    error: thisCompanyError,
  } = useTSQCompany(
    recId,
    companyType as CompanyType,
    formMode !== "create" && empOpts.length > 0,
  );

  const dataLoading =
    formMode === "create"
      ? empOpts.length === 0
      : empOpts.length === 0 || !refOrigData.current;

  const {
    data: thisVendOrders,
    isLoading: poLoading,
    error: poError,
  } = useTSQPurchOrdersByVendor(
    {
      page: orderTablePagination.page,
      page_size: orderTablePagination.size,
      sort_by: "purchase_orders.order_date DESC",
      filter: `purchase_orders.vendor_id = '${recId}'`,
    },
    !!recId && companyType === "vendor",
  );

  const {
    data: thisCustOrders,
    isLoading: soLoading,
    error: soError,
  } = useTSQSalesOrdersByCustomer(
    {
      page: orderTablePagination.page,
      page_size: orderTablePagination.size,
      sort_by: "sales_orders.order_date DESC",
      filter: `sales_orders.customer_id = '${recId}'`,
    },
    "company_form",
    !!recId && companyType === "customer",
  );

  const ordersLoading = soLoading || poLoading;

  const [allOrders, orderCount] = useMemo(() => {
    if (refFormMode.current !== "create") {
      if (refCompanyType.current === "customer") {
        if (thisCustOrders?.items) {
          return [thisCustOrders.items, thisCustOrders.total];
        }
      } else {
        if (thisVendOrders?.items) {
          return [thisVendOrders.items, thisVendOrders.total];
        }
      }
    }
    return [[], 0];
  }, [thisCustOrders, thisVendOrders]);

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<CompanyFormData>({
    resolver: yupResolver(companySchema) as any,
    context: { compType: companyType },
    defaultValues: formDefaults,
  });

  const [watchedCode, watchedName] = watch(["code", "name"]);

  const {
    fields: addrFields,
    append: addrAppend,
    remove: addrRemove,
    replace: addrReset,
    update: addrUpdate,
  } = useFieldArray({
    control,
    name: "addresses",
  });

  const {
    countries,
    states,
    cities,
    anyLoading: worldLoading,
    anyError: worldError,
  } = useWorldLocationData();

  const [countryOpts, stateOpts, cityOpts] = useMemo(() => {
    let countryOpts: ACountryOption[] = [];
    let stateOpts: AStateOption[] = [];
    let cityOpts: ACityOption[] = [];
    if (countries.data) {
      countryOpts = countries.data.items.map((c) => ({
        label: c.name,
        type: "worldcountry",
        value: {
          id: c.id,
          code: c.iso2,
        },
      }));
    }
    if (states.data) {
      stateOpts = states.data.items.map((s) => ({
        label: s.name,
        type: "worldstate",
        value: {
          id: s.id,
          code: s.iso2,
          country_id: s.country_id,
        },
      }));
    }
    if (cities.data) {
      cityOpts = cities.data.items.map((c) => ({
        label: c.name,
        type: "worldcity",
        value: {
          id: c.id,
          state_id: c.state_id,
          country_id: c.country_id,
        },
      }));
    }
    return [countryOpts, stateOpts, cityOpts];
  }, [countries, states, cities]);

  const locsLoaded =
    countryOpts.length > 0 && stateOpts.length > 0 && cityOpts.length > 0;

  const {
    data: thisCompanyAddresses,
    isLoading: addrsLoading,
    isFetching: addrsFetching,
    error: addrError,
  } = useTSQAddressList(
    {
      active_status: "all",
      filters: [[{ field: "entity_id", operator: "==", value: recId }]],
    },
    formMode !== "create" && !!recId && locsLoaded && !!refOrigData.current,
  );

  const resetAddresses = useCallback(
    (data: PaginatedAddressResponse | null) => {
      if (data) {
        const allAddrData: AddrData[] = data.items.map((addr) => {
          const countryOpt =
            countryOpts.find(
              (o) =>
                o.value.id === addr.country_obj.id ||
                o.label === addr.country ||
                o.value.code === addr.country,
            ) || null;
          const stateOpt =
            stateOpts.find((o) => {
              return (
                (o.value.id === addr.state_obj.id ||
                  o.label === addr.state ||
                  o.value.code === addr.state) &&
                o.value.country_id === countryOpt?.value.id
              );
            }) || null;
          const cityOpt =
            cityOpts.find((o) => {
              return (
                (o.value.id === addr.city_obj.id || o.label === addr.city) &&
                o.value.country_id === countryOpt?.value.id &&
                o.value.state_id === stateOpt?.value.id
              );
            }) || null;
          const addrPayload: AddrData = {
            addr_id: addr.id,
            name: addr.name,
            addressee: addr.addressee || null,
            attn: addr.attn || null,
            line_1: addr.line_1,
            line_2: addr.line_2 || null,
            line_3: addr.line_3 || null,
            city: cityOpt,
            state: stateOpt,
            country: countryOpt,
            zip: addr.zip,
            phone: addr.phone || null,
            email: addr.email || null,
            notes: addr.notes || null,
            is_preferred: addr.is_preferred,
            address_type: addressTypeOptions.find(
              (o) => o.value.id === addr.address_type,
            )!,
            is_delete: false,
            is_new: false,
            is_active: addr.is_record_active,
          };

          return addrPayload;
        });
        addrReset(allAddrData);
        refOrigAddrs.current = data.items;
      } else {
        addrReset([]);
        refOrigAddrs.current = [];
      }
    },
    [countryOpts, stateOpts, cityOpts, addrReset],
  );

  useEffect(() => {
    console.log(
      "ADDRESS USEEFFECT",
      refRenderCount.current,
      refFormMode.current,
    );
    if (refFormMode.current !== "create") {
      if (thisCompanyAddresses?.items) {
        resetAddresses(thisCompanyAddresses);
      }
    }
  }, [thisCompanyAddresses, resetAddresses]);

  const {
    fields: contactFields,
    append: contactAppend,
    remove: contactRemove,
    replace: contactReset,
    update: contactUpdate,
  } = useFieldArray({
    control,
    name: "contacts",
  });

  const resetForm = useCallback(
    (data: CompanyFullGroup | null) => {
      if (data) {
        let dataObj: CompanyFormData = JSON.parse(JSON.stringify(formDefaults));
        dataObj.name = data.name;
        dataObj.legal_name = data.legal_name;
        dataObj.code = data.code;
        dataObj.default_payment_terms =
          paymentTermOptions.find(
            (o) => o.value.id === data.default_payment_terms,
          ) || null;
        dataObj.credit_limit = parseFloat(data.credit_limit);
        dataObj.preferred_shipping_provider =
          shippingProviderOptions.find(
            (o) =>
              o.value.id === data.preferred_shipping_provider?.toUpperCase(),
          ) || null;
        let fedexOpt =
          fedexOpts.find(
            (o) =>
              o.value.id ===
              data.preferred_shipping_methods?.fedex?.toUpperCase(),
          ) || null;
        console.log("ALL FEDEX OPTS", fedexOpts);
        console.log(
          "FOUND FEDEX OPT",
          fedexOpt,
          data.preferred_shipping_methods?.fedex,
        );
        let upsOpt =
          upsOpts.find(
            (o) =>
              o.value.id ===
              data.preferred_shipping_methods?.ups?.toUpperCase(),
          ) || null;
        console.log("ALL UPS OPTS", upsOpts);
        console.log(
          "FOUND UPS OPT",
          upsOpt,
          data.preferred_shipping_methods?.ups,
        );
        dataObj.preferred_shipping_methods = {
          fedex: fedexOpt,
          ups: upsOpt,
        };
        dataObj.centese_contact =
          empOpts.find((o) => o.value.id === data.centese_contact?.id) || null;
        dataObj.box_folder_id = data.box_folder_id || null;
        dataObj.notes = data.notes || null;
        if (refCompanyType.current === "customer") {
          dataObj.customer_type =
            customerTypeOptions.find(
              (o) => o.value.id === (data as CustomerFull).customer_type,
            ) || null;
          dataObj.shipping_accounts = (data as CustomerFull).shipping_accounts;
          dataObj.shipment_billing_preference =
            customerShipmentBillingOptions.find(
              (o) =>
                o.value.id ===
                (data as CustomerFull).shipment_billing_preference,
            ) || null;
        }
        reset(dataObj);
        refOrigData.current = data;
      } else {
        reset(formDefaults);
        refOrigData.current = null;
      }
    },
    [empOpts, reset],
  );

  useEffect(() => {
    console.log("DA BIG USEEFFECT", refFormMode, thisCompanyData);
    if (refFormMode.current !== "create") {
      if (thisCompanyData) {
        resetForm(thisCompanyData);
      }
    }
  }, [thisCompanyData, resetForm]);

  // Tab Functions
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurTab(newValue);
  };

  const handleOrderTablePageChange = (event: unknown, newPage: number) => {
    setOrderTablePagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleOrderTableRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderTablePagination({
      size: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const [addrDialogOpen, setAddrDialogOpen] = useState<boolean>(false);
  const [addrDialogData, setAddrDialogData] = useState<AddrData | null>(null);
  const [addrDialogIndex, setAddrDialogIndex] = useState<number>(-1);

  const handleAddrDialogOpen = (data: AddrData, index: number) => {
    setAddrDialogData(data);
    setAddrDialogIndex(index);
    setAddrDialogOpen(true);
  };

  const handleAddrDialogCancel = () => {
    setAddrDialogOpen(false);
    setAddrDialogData(null);
    setAddrDialogIndex(-1);
  };

  const handleAddrDialogSubmit = (data: AddrData) => {
    if (addrDialogIndex === -1) {
      addrAppend(data);
    } else {
      addrUpdate(addrDialogIndex, data);
    }
    setAddrDialogOpen(false);
    setAddrDialogData(null);
    setAddrDialogIndex(-1);
  };

  const handlePageCancel = () => {
    window.open(`/companies?type=${companyType}`, "_self");
  };

  const switchToViewMode = () => {
    window.open(
      `/companies/form?mode=view&id=${recId}&type=${companyType}`,
      "_self",
    );
  };

  const switchToEditMode = () => {
    if (thisCompanyData) resetForm(thisCompanyData);
    window.open(
      `/companies/form?mode=edit&id=${recId}&type=${companyType}`,
      "_self",
    );
  };

  const switchToCreateMode = () => {
    window.open(`/companies/form?mode=create&type=${companyType}`, "_self");
  };

  const [processDialogOpen, setProcessDialogOpen] = useState<boolean>(false);

  const handleProcessDialogClose = (
    event: any,
    reason: string,
    navToView: boolean,
    recordId: string,
  ) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    setProcessDialogOpen(false);
    if (navToView) {
      window.open(
        `/companies/form?mode=view&id=${recordId}&type=${companyType}`,
        "_self",
      );
    }
  };

  const onSubmit = async (data: CompanyFormData) => {
    console.log("SUBMITTED DATA", data);
    try {
      setErrorText(null);
      setProcessDialogOpen(true);
      if (formMode === "create") {
        const newContacts: ContactCreate[] = [];

        const newAddresses: AddressCreate[] = data.addresses.map((dataLine) => {
          const cityObj: WorldLocation<"city"> = {
            type: "city",
            parent: dataLine.city!.value.state_id,
            name: dataLine.city!.label,
            id: dataLine.city!.value.id,
            code: dataLine.city!.label,
          };
          const countryObj: WorldLocation<"country"> = {
            type: "country",
            parent: -1,
            name: dataLine.country!.label,
            id: dataLine.country!.value.id,
            code: dataLine.country!.value.code,
          };
          const stateObj: WorldLocation<"state"> = {
            type: "state",
            parent: dataLine.state!.value.country_id,
            name: dataLine.state!.label,
            id: dataLine.state!.value.id,
            code: dataLine.state!.value.code,
          };
          const addrPayload: AddressCreate = {
            name: dataLine.name!,
            addressee: dataLine.addressee,
            attn: dataLine.attn,
            line_1: dataLine.line_1!,
            line_2: dataLine.line_2,
            line_3: dataLine.line_3,
            city: cityObj.name,
            city_obj: cityObj,
            state: stateObj.name,
            state_obj: stateObj,
            country: countryObj.code,
            country_obj: countryObj,
            zip: dataLine.zip!,
            phone: dataLine.phone,
            email: dataLine.email,
            notes: dataLine.notes,
            is_preferred: dataLine.is_preferred,
            address_type: dataLine.address_type!.value.id as AddressType,
            entity_id: null,
            entity_table: null,
          };
          return addrPayload;
        });

        if (companyType === "customer") {
          const custPayload: CustomerCreate = {
            name: data.name,
            legal_name: data.legal_name,
            company_type: "customer",
            customer_type: data.customer_type!.value.id as CustomerType,
            code: data.code !== "" ? data.code : null,
            default_payment_terms: data.default_payment_terms!.value
              .id as PaymentTermType,
            credit_limit: data.credit_limit!,
            preferred_shipping_provider:
              data.preferred_shipping_provider!.value.id,
            preferred_shipping_methods: {
              fedex: data.preferred_shipping_methods.fedex
                ? data.preferred_shipping_methods.fedex.value.id
                : null,
              ups: data.preferred_shipping_methods.ups
                ? data.preferred_shipping_methods.ups.value.id
                : null,
            },
            centese_contact_id: data.centese_contact
              ? data.centese_contact.value.id
              : null,
            shipping_accounts: data.shipping_accounts,
            shipment_billing_preference: data.shipment_billing_preference!.value
              .id as CustomerShipmentBillingType,
            notes: data.notes,
            new_contacts: newContacts,
            new_addresses: newAddresses,
            box_folder_id: data.box_folder_id,
          };
          const new_ent = await createCompanyMutation.mutateAsync(custPayload);
          handleProcessDialogClose(null, "create_complete", true, new_ent.id);
        } else {
          const vendPayload: VendorCreate = {
            name: data.name!,
            legal_name: data.legal_name!,
            company_type: "vendor",
            code: data.code,
            default_payment_terms: data.default_payment_terms!.value
              .id as PaymentTermType,
            credit_limit: data.credit_limit!,
            notes: data.notes || null,
            new_contacts: newContacts,
            new_addresses: newAddresses,
            preferred_shipping_provider: data.preferred_shipping_provider
              ? data.preferred_shipping_provider.value.id
              : null,
            box_folder_id: data.box_folder_id || null,
            preferred_shipping_methods: null,
            centese_contact_id: null,
          };
          const new_ent = await createCompanyMutation.mutateAsync(vendPayload);
          handleProcessDialogClose(null, "create_complete", true, new_ent.id);
        }
      } else if (formMode === "edit") {
        console.log("EDIT");
        if (recId === null) {
          setErrorText("Missing Record ID, cannot perform Update. Notify IT.");
          return;
        }
        if (refOrigData.current === null) {
          setErrorText(
            "Missing reference to original Company Data, cannot perform Update. Notify IT.",
          );
          return;
        }
        const newAddrs: AddressCreate[] = [];
        const updatedAddrs: AddressUpdate[] = [];
        const deletedAddrs: string[] = [];
        const reactivatedAddrs: string[] = [];
        data.addresses.forEach((addr, ind) => {
          if (addr.is_new) {
            newAddrs.push({
              name: addr.name || addr.line_1,
              addressee: addr.addressee,
              attn: addr.attn,
              line_1: addr.line_1,
              line_2: addr.line_2,
              line_3: addr.line_3,
              country: addr.country!.value.code,
              country_obj: {
                type: "country",
                parent: -1,
                name: addr.country!.label,
                code: addr.country!.value.code,
                id: addr.country!.value.id,
              },
              state: addr.state!.label,
              state_obj: {
                type: "state",
                parent: addr.state!.value.country_id,
                name: addr.state!.label,
                code: addr.state!.value.code,
                id: addr.state!.value.id,
              },
              city: addr.city!.label,
              city_obj: {
                type: "city",
                parent: addr.city!.value.state_id,
                name: addr.city!.label,
                code: addr.city!.label,
                id: addr.city!.value.id,
              },
              zip: addr.zip,
              phone: addr.phone,
              email: addr.email,
              notes: addr.notes,
              is_preferred: addr.is_preferred,
              address_type: addr.address_type!.value.id,
              entity_id: recId,
              entity_table:
                companyType === "customer" ? "customers" : "vendors",
            });
          } else if (addr.is_delete && addr.is_active) {
            deletedAddrs.push(addr.addr_id!);
          } else {
            const origAddr = refOrigAddrs.current[ind];
            const updateObj: Omit<AddressUpdate, "id"> = {};
            if (addr.addr_id !== origAddr.id) {
              console.error("Original Address ID mismatch", addr, origAddr);
              return true;
            }
            if (addr.name !== origAddr.name) updateObj["name"] = addr.name;
            if (addr.addressee !== (origAddr.addressee || null))
              updateObj["addressee"] = addr.addressee;
            if (addr.attn !== (origAddr.attn || null))
              updateObj["attn"] = addr.attn;
            if (addr.line_1 !== origAddr.line_1)
              updateObj["line_1"] = addr.line_1;
            if (addr.line_2 !== (origAddr.line_2 || null))
              updateObj["line_2"] = addr.line_2;
            if (addr.line_3 !== (origAddr.line_3 || null))
              updateObj["line_3"] = addr.line_3;
            if (addr.city && addr.city.label !== origAddr.city_obj.name) {
              updateObj["city"] = addr.city.label;
              updateObj["city_obj"] = {
                type: "city",
                parent: addr.city!.value.state_id,
                name: addr.city!.label,
                code: addr.city!.label,
                id: addr.city!.value.id,
              };
            }
            if (addr.state && addr.state.label !== origAddr.state_obj.name) {
              updateObj["state"] = addr.state.label;
              updateObj["state_obj"] = {
                type: "state",
                parent: addr.state!.value.country_id,
                name: addr.state!.label,
                code: addr.state!.value.code,
                id: addr.state!.value.id,
              };
            }
            if (
              addr.country &&
              addr.country.label !== origAddr.country_obj.name
            ) {
              updateObj["country"] = addr.country.label;
              updateObj["country_obj"] = {
                type: "country",
                parent: -1,
                name: addr.country!.label,
                code: addr.country!.value.code,
                id: addr.country!.value.id,
              };
            }
            if (addr.zip !== origAddr.zip) updateObj["zip"] = addr.zip;
            if (addr.phone !== (origAddr.phone || null))
              updateObj["phone"] = addr.phone;
            if (addr.email !== (origAddr.email || null))
              updateObj["email"] = addr.email;
            if (addr.notes !== origAddr.notes) updateObj["notes"] = addr.notes;
            if (addr.is_preferred !== origAddr.is_preferred)
              updateObj["is_preferred"] = addr.is_preferred;
            if (addr.address_type.value.id !== origAddr.address_type)
              updateObj["address_type"] = addr.address_type.value.id;
            if (!addr.is_active && addr.is_delete) {
              updateObj["is_record_active"] = true;
            }
            if (Object.keys(updateObj).length > 0) {
              updatedAddrs.push({ id: addr.addr_id, ...updateObj });
            }
          }
        });
        if (companyType === "customer") {
          const origCust = refOrigData.current as CustomerFull;
          const custPayload: CustomerUpdate = {
            name: data.name !== origCust.name ? data.name : undefined,
            company_type: "customer",
            notes: data.notes !== origCust.notes ? data.notes : undefined,
            code: data.code !== origCust.code ? data.code : undefined,
            default_payment_terms:
              data.default_payment_terms!.value.id !==
              origCust.default_payment_terms
                ? data.default_payment_terms!.value.id
                : undefined,
            credit_limit:
              data.credit_limit !== Number(origCust.credit_limit)
                ? data.credit_limit
                : undefined,
            preferred_shipping_provider:
              data.preferred_shipping_provider!.value.id !==
              origCust.preferred_shipping_provider
                ? data.preferred_shipping_provider!.value.id
                : undefined,
            preferred_shipping_methods:
              data.preferred_shipping_methods.ups?.value.id !==
                origCust.preferred_shipping_methods?.ups ||
              data.preferred_shipping_methods.fedex?.value.id !==
                origCust.preferred_shipping_methods?.fedex
                ? {
                    fedex:
                      data.preferred_shipping_methods["fedex"]?.value.id ||
                      null,
                    ups:
                      data.preferred_shipping_methods["ups"]?.value.id || null,
                  }
                : undefined,
            centese_contact_id:
              data.centese_contact!.value.id !== origCust.centese_contact!.id
                ? data.centese_contact!.value.id
                : undefined,
            shipping_accounts:
              data.shipping_accounts.fedex !==
                origCust.shipping_accounts.fedex ||
              data.shipping_accounts.ups !== origCust.shipping_accounts.ups
                ? {
                    fedex: data.shipping_accounts["fedex"],
                    ups: data.shipping_accounts["ups"],
                  }
                : undefined,
            customer_type:
              data.customer_type!.value.id !== origCust.customer_type
                ? data.customer_type!.value.id
                : undefined,
            shipment_billing_preference:
              data.shipment_billing_preference!.value.id !==
              origCust.shipment_billing_preference
                ? data.shipment_billing_preference!.value.id
                : undefined,
            new_contacts: [],
            updated_contacts: [],
            deleted_contacts: [],
            new_addresses: newAddrs,
            updated_addresses: updatedAddrs,
            deleted_addresses: deletedAddrs,
          };
          const updated_ent = await updateCompanyMutation.mutateAsync({
            id: recId,
            data: custPayload,
          });
          handleProcessDialogClose(
            null,
            "update_complete",
            true,
            updated_ent.id,
          );
        } else {
          console.log(companyType);
          const origVend = refOrigData.current as VendorFull;
          const vendPayload: VendorUpdate = {
            name: data.name !== origVend.name ? data.name : undefined,
            company_type: "vendor",
            notes: data.notes !== origVend.notes ? data.notes : undefined,
            code: data.code !== origVend.code ? data.code : undefined,
            default_payment_terms:
              data.default_payment_terms!.value.id !==
              origVend.default_payment_terms
                ? data.default_payment_terms!.value.id
                : undefined,
            credit_limit:
              data.credit_limit !== Number(origVend.credit_limit)
                ? data.credit_limit
                : undefined,
            preferred_shipping_provider:
              data.preferred_shipping_provider!.value.id !==
              origVend.preferred_shipping_provider
                ? data.preferred_shipping_provider!.value.id
                : undefined,
            preferred_shipping_methods:
              data.preferred_shipping_methods.ups?.value.id !==
                origVend.preferred_shipping_methods?.ups ||
              data.preferred_shipping_methods.fedex?.value.id !==
                origVend.preferred_shipping_methods?.fedex
                ? {
                    fedex:
                      data.preferred_shipping_methods["fedex"]?.value.id ||
                      null,
                    ups:
                      data.preferred_shipping_methods["ups"]?.value.id || null,
                  }
                : undefined,
            centese_contact_id:
              data.centese_contact!.value.id !== origVend.centese_contact!.id
                ? data.centese_contact!.value.id
                : undefined,
            new_contacts: [],
            updated_contacts: [],
            deleted_contacts: [],
            new_addresses: newAddrs,
            updated_addresses: updatedAddrs,
            deleted_addresses: deletedAddrs,
          };
          const updated_ent = await updateCompanyMutation.mutateAsync({
            id: recId,
            data: vendPayload,
          });
          handleProcessDialogClose(
            null,
            "update_complete",
            true,
            updated_ent.id,
          );
        }
      }
    } catch (err: any) {
      setErrorText(err.message || "Failed to save company");
      handleProcessDialogClose(null, "submit_failed", false, "");
    }
  };

  if (dataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Alert severity="info">Loading Data...</Alert>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack direction="column" width="100%" sx={{ p: 3 }}>
      <Typography variant="h4" component="h1">
        {formMode === "create"
          ? `Create New ${companyTypePretty}`
          : `View ${companyTypePretty}: ${
              thisCompanyData?.name || "UNKNOWN COMPANY"
            }`}
      </Typography>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handlePageCancel}
          variant="outlined"
        >
          {`Back to ${companyTypePretty}s`}
        </Button>
        {formMode === "view" && (
          <Button
            variant="outlined"
            onClick={switchToCreateMode}
            startIcon={<AddIcon />}
          >
            Create New
          </Button>
        )}
        {formMode === "view" && (
          <Button
            variant="outlined"
            onClick={switchToEditMode}
            startIcon={<AddIcon />}
          >
            Edit
          </Button>
        )}
        {formMode === "edit" && (
          <Button
            variant="outlined"
            onClick={switchToViewMode}
            startIcon={<AddIcon />}
          >
            View
          </Button>
        )}
        {user?.is_superuser && (
          <Button
            variant="outlined"
            onClick={() => {
              console.log({
                mode: formMode,
                formVals: getValues(),
                origData: refOrigData.current,
                origAddrs: refOrigAddrs.current,
                shippingOpts: {
                  fedex: fedexOpts,
                  ups: upsOpts,
                },
              });
            }}
          >
            DEBUG PRINT
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit, console.error)}>
          <Stack direction="column" spacing={3}>
            {!(formMode === "view") && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handlePageCancel}
                  disabled={isSubmitting}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : formMode === "create"
                      ? `Create ${companyTypePretty}`
                      : `Update ${companyTypePretty}`}
                </Button>
              </Box>
            )}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Stack direction="column" spacing={1} width="50%">
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { value, onChange, ...restField } }) => (
                    <TextField
                      {...restField}
                      label="Name"
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        if (!overrideLegalName) {
                          if (onLegalNameChange !== null) {
                            onLegalNameChange(e.target.value);
                          }
                          // setValue("legal_name", e.target.value);
                        }
                      }}
                      fullWidth
                      disabled={formMode === "view"}
                      error={!!formErrors.name}
                      helperText={formErrors.name?.message || ""}
                    />
                  )}
                />
                <Controller
                  name="legal_name"
                  control={control}
                  render={({ field: { value, onChange, ...restField } }) => {
                    onLegalNameChange = onChange;
                    return (
                      <TextField
                        {...restField}
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                        }}
                        label="Legal Name"
                        fullWidth
                        disabled={formMode === "view" || !overrideLegalName}
                        error={!!formErrors.legal_name}
                        helperText={formErrors.legal_name?.message || ""}
                        slotProps={{
                          inputLabel: { shrink: !!value },
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip
                                  title={
                                    overrideLegalName
                                      ? "Lock Legal Name to Name"
                                      : "Manual Legal Name"
                                  }
                                >
                                  <IconButton
                                    onClick={() => {
                                      if (overrideLegalName) {
                                        onLegalNameChange(watchedName);
                                      }
                                      setOverrideLegalName(!overrideLegalName);
                                    }}
                                    edge="end"
                                    size="small"
                                    disabled={formMode === "view"}
                                  >
                                    {overrideLegalName ? (
                                      <LockIcon />
                                    ) : (
                                      <EditIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reset Legal Name">
                                  <IconButton
                                    onClick={() => {
                                      if (overrideLegalName)
                                        setOverrideLegalName(false);
                                      onLegalNameChange(watchedName);
                                    }}
                                    edge="end"
                                    size="small"
                                    disabled={
                                      formMode === "view" || !overrideLegalName
                                    }
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    );
                  }}
                />
              </Stack>

              <Controller
                name="code"
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <TextField
                    {...restField}
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    sx={{ width: "50%" }}
                    label={`${companyTypePretty} Code`}
                    disabled={formMode === "view" || !overrideCodeGen}
                    error={!!formErrors.code}
                    helperText={
                      formErrors.code?.message
                        ? formErrors.code.message
                        : formMode === "create" && !overrideCodeGen
                          ? `Code will be Auto Generated`
                          : undefined
                    }
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title={
                                overrideCodeGen
                                  ? "Lock Code"
                                  : formMode === "create"
                                    ? "Override Code Generation"
                                    : "Update Code"
                              }
                            >
                              <IconButton
                                onClick={() =>
                                  setOverrideCodeGen(!overrideCodeGen)
                                }
                                edge="end"
                                size="small"
                                disabled={formMode === "view"}
                              >
                                {overrideCodeGen ? <LockIcon /> : <EditIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset WO#">
                              <IconButton
                                onClick={() => {
                                  if (overrideCodeGen)
                                    setOverrideCodeGen(false);
                                  setValue("code", refOrigData.current!.code);
                                }}
                                edge="end"
                                size="small"
                                disabled={
                                  formMode === "view" ||
                                  refOrigData.current?.code === watchedCode
                                }
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Divider />

            <Typography variant="h6" gutterBottom>
              Details
            </Typography>

            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <Stack direction="column" gap={2} width="50%">
                {companyType === "customer" && (
                  <Controller
                    name="centese_contact"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => {
                      return (
                        <Autocomplete
                          {...restField}
                          value={value}
                          onChange={(event, newValue) => {
                            onChange(newValue);
                          }}
                          options={empOpts}
                          disabled={formMode === "view"}
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value?.value
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              label="Sales Rep"
                              error={!!formErrors.centese_contact}
                              helperText={formErrors.centese_contact?.message}
                            />
                          )}
                        />
                      );
                    }}
                  />
                )}
                <Controller
                  name="default_payment_terms"
                  control={control}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <Autocomplete
                      {...restField}
                      value={value}
                      onChange={(event, newValue) => {
                        onChange(newValue);
                      }}
                      options={paymentTermOptions}
                      disabled={formMode === "view"}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Default Payment Terms"
                          disabled={formMode === "view"}
                          error={!!formErrors.default_payment_terms}
                          helperText={formErrors.default_payment_terms?.message}
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="credit_limit"
                  control={control}
                  render={({ field: { value, onChange, ...restField } }) => (
                    <NumericEntryField
                      value={value}
                      onChange={(v) => {
                        onChange(v);
                      }}
                      label="Credit Limit"
                      disabled={formMode === "view"}
                      isCurrency
                      decimalPlaces={2}
                      textFieldProps={{
                        ...restField,
                        fullWidth: true,
                        error: !!formErrors.credit_limit,
                        helperText: formErrors.credit_limit?.message,
                      }}
                    />
                  )}
                />
                {companyType === "customer" && (
                  <Controller
                    name="customer_type"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        value={value}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                        }}
                        options={customerTypeOptions}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value?.value
                        }
                        disabled={formMode === "view"}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            label="Customer Type"
                            disabled={formMode === "view"}
                            error={!!formErrors.customer_type}
                            helperText={formErrors.customer_type?.message}
                          />
                        )}
                      />
                    )}
                  />
                )}
              </Stack>
              <Controller
                name="notes"
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <TextField
                    {...restField}
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Notes"
                    multiline
                    rows={8}
                    fullWidth
                    disabled={formMode === "view"}
                    error={!!formErrors.notes}
                    helperText={
                      formErrors.notes?.message ||
                      `Optional notes about this ${stringUtils.capFirstLetter(
                        companyType,
                      )}`
                    }
                  />
                )}
              />
            </Box>

            <Divider />

            <Typography variant="h6" gutterBottom>
              Shipping Options
            </Typography>

            <Stack direction="column" gap={2}>
              <Box display="flex" gap={2}>
                <Controller
                  name="preferred_shipping_provider"
                  control={control}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <Autocomplete
                      {...restField}
                      fullWidth
                      id="preferred_shipping_provider"
                      value={value}
                      onChange={(event, newValue) => {
                        onChange(newValue);
                      }}
                      options={shippingProviderOptions}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                      }
                      disabled={formMode === "view"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Shipping Provider"
                          disabled={formMode === "view"}
                          error={!!formErrors.preferred_shipping_provider}
                          helperText={
                            formErrors.preferred_shipping_provider?.message
                          }
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="shipment_billing_preference"
                  control={control}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <Autocomplete
                      {...restField}
                      fullWidth
                      value={value}
                      onChange={(event, newValue) => {
                        onChange(newValue);
                      }}
                      options={customerShipmentBillingOptions}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                      }
                      disabled={formMode === "view"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Shipment Billing Option"
                          error={!!formErrors.shipment_billing_preference}
                          helperText={
                            formErrors.shipment_billing_preference?.message
                          }
                        />
                      )}
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gridTemplateRows: "auto auto auto auto",
                }}
                gap={2}
              >
                <Stack
                  direction="column"
                  gap={0.5}
                  sx={{ gridColumn: 1, gridRow: 1 }}
                >
                  <Typography variant="body1">Ship Methods</Typography>
                  {formErrors.preferred_shipping_methods?.message !==
                    undefined && (
                    <Alert severity="error">
                      {formErrors.preferred_shipping_methods.message}
                    </Alert>
                  )}
                </Stack>
                <Controller
                  name="preferred_shipping_methods.fedex"
                  control={control}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <Autocomplete
                      {...restField}
                      sx={{ gridColumn: 1, gridRow: 2 }}
                      value={value}
                      onChange={(event, newValue) => {
                        onChange(newValue);
                      }}
                      options={fedexOpts}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                      }
                      disabled={formMode === "view"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Preferred FedEx Shipping Method"
                          error={!!formErrors.preferred_shipping_methods?.fedex}
                          helperText={
                            formErrors.preferred_shipping_methods?.fedex
                              ?.message
                          }
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="preferred_shipping_methods.ups"
                  control={control}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <Autocomplete
                      {...restField}
                      sx={{ gridColumn: 1, gridRow: 3 }}
                      value={value}
                      onChange={(event, newValue) => {
                        onChange(newValue);
                      }}
                      options={upsOpts}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                      }
                      disabled={formMode === "view"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Preferred UPS Shipping Method"
                          error={!!formErrors.preferred_shipping_methods?.ups}
                          helperText={
                            formErrors.preferred_shipping_methods?.ups?.message
                          }
                        />
                      )}
                    />
                  )}
                />
                <Stack
                  direction="column"
                  gap={0.5}
                  sx={{ gridColumn: 2, gridRow: 1 }}
                >
                  <Typography variant="body1">Accounts</Typography>
                  {!!formErrors.shipping_accounts?.message && (
                    <Alert severity="error">
                      {formErrors.shipping_accounts.message}
                    </Alert>
                  )}
                </Stack>
                <Controller
                  name="shipping_accounts.fedex"
                  control={control}
                  render={({ field: { value, onChange, ...restField } }) => (
                    <TextField
                      {...restField}
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      sx={{ gridColumn: 2, gridRow: 2 }}
                      label="FedEx"
                      error={!!formErrors.shipping_accounts?.fedex}
                      helperText={formErrors.shipping_accounts?.fedex?.message}
                      disabled={formMode === "view"}
                    />
                  )}
                />
                <Controller
                  name="shipping_accounts.ups"
                  control={control}
                  render={({ field: { value, onChange, ...restField } }) => (
                    <TextField
                      {...restField}
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      sx={{ gridColumn: 2, gridRow: 3 }}
                      label="UPS"
                      error={!!formErrors.shipping_accounts?.ups}
                      helperText={formErrors.shipping_accounts?.ups?.message}
                      disabled={formMode === "view"}
                    />
                  )}
                />
              </Box>
            </Stack>

            <Divider />

            <Tabs value={curTab} onChange={handleTabChange}>
              <Tab label="Addresses" />
              {formMode !== "create" && (
                <Tab
                  label={
                    companyType === "customer"
                      ? "Sales Orders"
                      : "Purchase Orders"
                  }
                />
              )}
              {/* <Tab label="Contacts" /> */}
            </Tabs>
            <Box display={curTab === 0 ? "flex-col" : "none"} gap={1}>
              {formMode !== "view" && (
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => handleAddrDialogOpen(defaultAddrData, -1)}
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={worldLoading}
                >
                  Add Address
                </Button>
              )}
              {formErrors.addresses?.message !== undefined && (
                <Alert severity="error">{formErrors.addresses.message}</Alert>
              )}
              {(addrsFetching || worldLoading) && (
                <Box
                  display="flex"
                  gap={0.5}
                  width="50%"
                  justifySelf={"center"}
                >
                  <Typography variant="h5">Loading Addresses...</Typography>
                  <CircularProgress />
                </Box>
              )}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="20%">Address Name</TableCell>
                      <TableCell width="20%">Addressee & Attention</TableCell>
                      <TableCell width="20%">Address</TableCell>
                      <TableCell width="20%">
                        City, State Zip
                        <br />
                        Country
                      </TableCell>
                      <TableCell width="20%"></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} align="left">
                        <Box display="flex" gap={0.5} alignItems={"center"}>
                          <StarIcon color="success" />
                          <Typography variant="h5">New Line</Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems={"center"}>
                          <DeleteIcon color="error" />
                          <Typography variant="h5">
                            Line Pending Deletion
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems={"center"}>
                          <Typography variant="body1" color="info">
                            Blue Text
                          </Typography>
                          <Typography variant="h5">
                            Value Pending Update
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {addrFields.map((addr, ind) => {
                      const origAddr = refOrigAddrs.current[ind];
                      return (
                        <TableRow
                          key={addr.addr_id}
                          sx={{
                            backgroundColor: addr.is_active
                              ? "inherit"
                              : "#8f8f8fff",
                          }}
                        >
                          <TableCell>
                            <Box display="flex" gap={0.5}>
                              {!origAddr || origAddr.name === addr.name ? (
                                <Typography variant="body1">
                                  {addr.name}
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  {addr.name}
                                </Typography>
                              )}
                              {addr.is_delete && <DeleteIcon color="error" />}
                              {addr.is_new && <StarIcon color="success" />}
                            </Box>
                            {!addr.is_active && (
                              <Typography variant="body1" color="error">
                                This address is inactive
                              </Typography>
                            )}
                            {!!formErrors.addresses &&
                              formErrors.addresses[ind] && (
                                <Stack direction="column" gap={0.5}>
                                  {formErrors.addresses[ind]?.line_1 && (
                                    <Alert severity="error">
                                      {
                                        formErrors.addresses[ind]?.line_1
                                          ?.message
                                      }
                                    </Alert>
                                  )}
                                  {formErrors.addresses[ind]?.country && (
                                    <Alert severity="error">
                                      {
                                        formErrors.addresses[ind]?.country
                                          ?.message
                                      }
                                    </Alert>
                                  )}
                                  {formErrors.addresses[ind]?.state && (
                                    <Alert severity="error">
                                      {
                                        formErrors.addresses[ind]?.state
                                          ?.message
                                      }
                                    </Alert>
                                  )}
                                  {formErrors.addresses[ind]?.city && (
                                    <Alert severity="error">
                                      {formErrors.addresses[ind]?.city?.message}
                                    </Alert>
                                  )}
                                  {formErrors.addresses[ind]?.zip && (
                                    <Alert severity="error">
                                      {formErrors.addresses[ind]?.zip?.message}
                                    </Alert>
                                  )}
                                </Stack>
                              )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="column" gap={1}>
                              {!origAddr ||
                              (origAddr.addressee || null) ===
                                addr.addressee ? (
                                <Typography variant="body1">
                                  Addressee: {addr.addressee || "-"}
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  Addressee: {addr.addressee || "-"}
                                </Typography>
                              )}
                              {!origAddr ||
                              (origAddr.attn || null) === addr.attn ? (
                                <Typography variant="body1">
                                  Attention: {addr.attn || "-"}
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  Attention: {addr.attn || "-"}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="column" gap={0.5}>
                              {!origAddr || origAddr.line_1 === addr.line_1 ? (
                                <Typography variant="body1">
                                  Line 1: {addr.line_1}
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  Line 1: {addr.line_1}
                                </Typography>
                              )}
                              {!origAddr || origAddr.line_2 === addr.line_2 ? (
                                <Typography variant="body1">
                                  Line 2: {addr.line_2 || "-"}
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  Line 2: {addr.line_2}
                                </Typography>
                              )}
                              {!origAddr || origAddr.line_3 === addr.line_3 ? (
                                <Typography variant="body1">
                                  Line 3: {addr.line_3 || "-"}
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  Line 3: {addr.line_3}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5}>
                              {!origAddr ||
                              origAddr.city === addr.city?.label ? (
                                <Typography variant="body1">
                                  {addr.city?.label},
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  {addr.city?.label},
                                </Typography>
                              )}
                              {!origAddr ||
                              origAddr.state === addr.state?.label ||
                              origAddr.state === addr.state?.value.code ? (
                                <Typography variant="body1">
                                  {addr.state?.label}
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  {addr.state?.label}
                                </Typography>
                              )}
                              {!origAddr || origAddr.zip === addr.zip ? (
                                <Typography variant="body1">
                                  {`${addr.zip}`}
                                </Typography>
                              ) : (
                                <Typography variant="body1" color="info">
                                  {`${addr.zip}`}
                                </Typography>
                              )}
                            </Box>
                            <br />
                            {!origAddr ||
                            origAddr.country === addr.country?.label ||
                            origAddr.country === addr.country?.value.code ? (
                              <Typography variant="body1">
                                {`${addr.country?.label}`}
                              </Typography>
                            ) : (
                              <Typography variant="body1" color="info">
                                {`${addr.country?.label}`}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {formMode !== "view" && (
                              <Stack direction="column" gap={1}>
                                <Button
                                  variant="outlined"
                                  color="info"
                                  onClick={() =>
                                    handleAddrDialogOpen(addr, ind)
                                  }
                                >
                                  Edit Address
                                </Button>
                                <Button
                                  variant="contained"
                                  color="error"
                                  onClick={() => {
                                    if (addr.is_new) {
                                      addrRemove(ind);
                                    } else {
                                      addrUpdate(ind, {
                                        ...addr,
                                        is_delete: !addr.is_delete,
                                      });
                                    }
                                  }}
                                >
                                  {addr.is_new
                                    ? "Delete New Address"
                                    : addr.is_active
                                      ? addr.is_delete
                                        ? "Cancel Deactivation"
                                        : "Mark For Deactivation"
                                      : addr.is_delete
                                        ? "Cancel Reactivation"
                                        : "Mark For Reactivation"}
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <AddressDialog
                open={addrDialogOpen}
                prevAddrData={addrDialogData}
                onCancel={handleAddrDialogCancel}
                onSubmit={handleAddrDialogSubmit}
                countryOpts={countryOpts}
                stateOpts={stateOpts}
                cityOpts={cityOpts}
              />
            </Box>
            <Box display={curTab === 1 ? "flex-col" : "none"} gap={1}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={orderCount}
                rowsPerPage={orderTablePagination.size}
                page={orderTablePagination.page}
                onPageChange={handleOrderTablePageChange}
                onRowsPerPageChange={handleOrderTableRowsPerPageChange}
              />
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="20%">Order Number</TableCell>
                      <TableCell width="20%">Dates</TableCell>
                      <TableCell width="20%">Status</TableCell>
                      <TableCell width="20%">Details</TableCell>
                      <TableCell width="20%"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ordersLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : allOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="textSecondary">
                            {`No orders found for this ${companyTypePretty}`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      allOrders.map((order) => {
                        return (
                          <TableRow key={order.id}>
                            <TableCell>
                              <Typography variant="body1">
                                {order.number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems={"center"} gap={1}>
                                <Typography variant="caption">
                                  Order Date:
                                </Typography>
                                <Typography variant="body1">
                                  {new Date(
                                    order.order_date,
                                  ).toLocaleDateString("en-US")}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems={"center"} gap={1}>
                                <Typography variant="caption">
                                  {companyType === "customer"
                                    ? "Requested Date:"
                                    : "Expected Date:"}
                                </Typography>
                                <Typography variant="body1">
                                  {order.alt_date
                                    ? new Date(
                                        order.alt_date,
                                      ).toLocaleDateString("en-US")
                                    : "N/A"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="column" gap={0.5}>
                                <Box
                                  display="flex"
                                  alignItems={"center"}
                                  gap={1}
                                >
                                  <Typography variant="caption">
                                    Order Status:
                                  </Typography>
                                  <Chip
                                    color={statusUtils.getPrimaryOrderStatusColor(
                                      order.order_status,
                                    )}
                                    label={stringUtils.capFirstLetter(
                                      order.order_status,
                                    )}
                                    size="small"
                                  />
                                </Box>
                                {companyType === "vendor" && (
                                  <Box
                                    display="flex"
                                    alignItems={"center"}
                                    gap={1}
                                  >
                                    <Typography variant="caption">
                                      Approval Status:
                                    </Typography>
                                    <Chip
                                      color={statusUtils.getApprovalStatusColor(
                                        order.approval_status,
                                      )}
                                      label={stringUtils.capFirstLetter(
                                        order.approval_status,
                                      )}
                                      size="small"
                                    />
                                  </Box>
                                )}
                                <Box
                                  display="flex"
                                  alignItems={"center"}
                                  gap={1}
                                >
                                  <Typography variant="caption">
                                    {`${
                                      companyType === "customer"
                                        ? "Fulfillment"
                                        : "Receipt"
                                    } Status:`}
                                  </Typography>
                                  <Chip
                                    color={
                                      companyType === "customer"
                                        ? statusUtils.getSoFulfillmentStatusColor(
                                            order.alt_status as SalesOrderFulfillmentStatusType,
                                          )
                                        : statusUtils.getPoReceiptStatusColor(
                                            order.alt_status as PurchaseOrderReceiptStatusType,
                                          )
                                    }
                                    label={stringUtils.capFirstLetter(
                                      order.alt_status,
                                    )}
                                    size="small"
                                  />
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="left">
                              <Stack direction="column" gap={0.5}>
                                <Box
                                  display="flex"
                                  alignItems={"center"}
                                  gap={1}
                                >
                                  <Typography variant="caption">
                                    Subtotal:
                                  </Typography>
                                  <NumericDisplayField
                                    value={parseFloat(order.subtotal)}
                                    decimalPlaces={4}
                                    isCurrency={true}
                                  />
                                </Box>
                                <Box
                                  display="flex"
                                  alignItems={"center"}
                                  gap={1}
                                >
                                  <Typography variant="caption">
                                    Items Ordered:
                                  </Typography>
                                  <Typography variant="body1">
                                    {order.items_ordered.join(", ")}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    navigate(
                                      `/orders/${
                                        companyType === "customer"
                                          ? "sales"
                                          : "purchase"
                                      }/form?mode=view&id=${order.id}`,
                                    )
                                  }
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Dialog
              open={processDialogOpen}
              onClose={(e, r) => {
                return;
              }}
              disableEscapeKeyDown
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Request Processing Status</DialogTitle>
              <DialogContent>
                {formMode === "create" ? (
                  <Stack direction="column" gap={3} p={1}>
                    <Box display="flex" gap={2} justifyContent="space-evenly">
                      <Typography variant="h5">Creating Company</Typography>
                      {createCompanyMutation.isPending ? (
                        <CircularProgress />
                      ) : createCompanyMutation.isSuccess ? (
                        <CheckIcon color="primary" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </Box>
                    {createCompanyMutation.isPending ? (
                      <Typography variant="h5">
                        Form will navigate to "View" mode once requests are
                        completed...
                      </Typography>
                    ) : (
                      <Typography variant="h5">
                        Requests completed, returning to "View" mode...
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Stack direction="column" gap={3}>
                    <Box display="flex" gap={2} justifyContent="space-evenly">
                      <Typography variant="h5">Updating Company</Typography>
                      {updateCompanyMutation.isPending ? (
                        <CircularProgress />
                      ) : updateCompanyMutation.isSuccess ? (
                        <CheckIcon color="primary" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </Box>
                    {updateCompanyMutation.isPending ? (
                      <Typography variant="h5">
                        Form will navigate to "View" mode once requests are
                        completed...
                      </Typography>
                    ) : (
                      <Typography variant="h5">
                        Requests completed, returning to "View" mode...
                      </Typography>
                    )}
                  </Stack>
                )}
              </DialogContent>
            </Dialog>
            {!(formMode === "view") && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handlePageCancel}
                  disabled={isSubmitting}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : formMode === "create"
                      ? `Create ${companyTypePretty}`
                      : `Update ${companyTypePretty}`}
                </Button>
              </Box>
            )}
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
};
